// Hồ sơ tàu / chuyến (Mint-Vessel): thời gian còn lại tới cut-off, việc cần làm, nhóm booking, lọc lịch sử.
// Mốc giờ trong dữ liệu là ISO có +07:00 nên so bằng Date (không đọc múi giờ máy).
import type { Vessel, VesselCall, VesselEvent, VoyageBooking } from './types';

/** "Bây giờ" của dữ liệu mẫu (board: 22/Oct/2026 14:00). TODO: giờ thật khi có dữ liệu thật. */
export const VESSEL_NOW = '2026-10-22T14:00:00+07:00';

export type RemainClass = 'none' | 'past' | 'soon' | 'ok';
export interface Remain {
  text: string;
  cls: RemainClass;
}

const HOUR = 3_600_000;

/** Còn bao lâu tới `iso`: < 24h → "Nh" (sắp tới) · < 72h → "N ngày Hh" · hơn → "N ngày"; đã qua → "đã qua". */
export function remaining(iso: string, now: string = VESSEL_NOW): Remain {
  const t = Date.parse(iso);
  const n = Date.parse(now);
  if (!iso || Number.isNaN(t) || Number.isNaN(n)) return { text: '', cls: 'none' };
  const h = (t - n) / HOUR;
  if (h < 0) return { text: 'đã qua', cls: 'past' };
  const d = Math.floor(h / 24);
  if (h < 24) return { text: `${Math.floor(h)}h`, cls: 'soon' };
  if (h < 72) return { text: `${d} ngày ${Math.floor(h % 24)}h`, cls: 'ok' };
  return { text: `${d} ngày`, cls: 'ok' };
}

export interface Cutoff {
  key: 'si' | 'icd' | 'cy' | 'etd';
  label: string;
  at: string;
  remain: Remain;
}

/** Các mốc của chuyến theo thứ tự thời gian: SI / Doc · ICD · CY · ETD. */
export function cutoffs(v: Pick<Vessel, 'si' | 'icd' | 'cy' | 'etd'>, now: string = VESSEL_NOW): Cutoff[] {
  const rows: [Cutoff['key'], string, string][] = [
    ['si', 'SI / Doc cut-off', v.si],
    ['icd', 'ICD cut-off', v.icd],
    ['cy', 'CY cut-off', v.cy],
    ['etd', 'ETD', v.etd],
  ];
  return rows.filter(([, , at]) => at).map(([key, label, at]) => ({ key, label, at, remain: remaining(at, now) })).sort((a, b) => a.at.localeCompare(b.at));
}

export type BookingGroup = 'Standby' | 'Active' | 'Released' | 'Huỷ';
const GROUP: Record<string, BookingGroup> = {
  'RQ Request': 'Standby',
  'RQ Standby': 'Standby',
  'BK Standby': 'Standby',
  'BK Confirmed': 'Active',
  'BL Hold': 'Active',
  'BL Wait RLS': 'Active',
  'BL Released': 'Released',
  'No Show': 'Huỷ',
  Cancelled: 'Huỷ',
};
export const bookingGroup = (status: string): BookingGroup => GROUP[status] ?? 'Huỷ';

export function groupCounts(bookings: readonly Pick<VoyageBooking, 'status'>[]): { group: BookingGroup; n: number }[] {
  return (['Standby', 'Active', 'Released', 'Huỷ'] as const).map((group) => ({ group, n: bookings.filter((b) => bookingGroup(b.status) === group).length })).filter((g) => g.n > 0);
}

/** `2×40RH` → { 40RH: 2 }; tổng theo loại cont của mọi booking. */
export function containerTotals(bookings: readonly Pick<VoyageBooking, 'cont'>[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const b of bookings) {
    const m = /^(\d+)×(\w+)$/.exec(b.cont);
    if (m) out[m[2] as string] = (out[m[2] as string] ?? 0) + Number(m[1]);
  }
  return out;
}

/** Job trễ chưa báo khách: tàu đã đổi lịch, booking chưa phải yêu cầu, chưa có mốc báo. */
export const unnotified = (v: Pick<Vessel, 'changes'>, bookings: readonly Pick<VoyageBooking, 'status' | 'notifiedAt'>[]): number =>
  v.changes > 0 ? bookings.filter((b) => !b.notifiedAt && b.status !== 'RQ Request').length : 0;

export interface Todo {
  key: string;
  text: string;
  tone: 'warn' | 'err' | 'ok' | 'muted';
  /** hành động đi kèm (chuyển tab) */
  go?: 'bk';
}

export function todos(v: Vessel, bookings: readonly VoyageBooking[], now: string = VESSEL_NOW): Todo[] {
  const out: Todo[] = [];
  const cy = remaining(v.cy, now);
  if (cy.cls !== 'none') out.push({ key: 'cy', text: `CY cut-off · ${cy.text}`, tone: cy.cls === 'soon' ? 'err' : cy.cls === 'past' ? 'muted' : 'warn', go: 'bk' });
  const n = unnotified(v, bookings);
  if (n) out.push({ key: 'ntf', text: `${n} job trễ chưa báo khách`, tone: 'warn' });
  out.push(v.atd ? { key: 'atd', text: `Đã ATD ${v.atd.slice(8, 10)}/${v.atd.slice(5, 7)}`, tone: 'ok' } : { key: 'atd', text: `ATD Confirm sau ETD ${v.etd.slice(8, 10)}/${v.etd.slice(5, 7)}`, tone: 'muted' });
  return out;
}

export type EventFilter = 'all' | VesselEvent['kind'];
export const EVENT_FILTERS: readonly { key: EventFilter; label: string }[] = [
  { key: 'all', label: 'Tất cả' },
  { key: 'chg', label: 'Đổi lịch' },
  { key: 'notice', label: 'Notice' },
  { key: 'bk', label: 'Booking' },
];
export const filterEvents = (events: readonly VesselEvent[], f: EventFilter): VesselEvent[] =>
  events.filter((e) => f === 'all' || e.kind === f).sort((a, b) => b.at.localeCompare(a.at));

/** Điểm dừng tiếp theo trong hải trình (cảng "Đang tới"). */
export const nextCall = (calls: readonly VesselCall[]): VesselCall | undefined => calls.find((c) => c.state === 'next');

export const hasProfile = (v: Pick<Vessel, 'profile'>): boolean => v.profile !== null;
